// supervisor-release-flow-resume-up-to-g3.ts
//
// The not_requested / waived / invalidated branch of
// `resumeReleaseFlowIfConfigured`, split out of supervisor-release-flow-resume.ts
// (200-line budget) when that module gained the deploy-state precheck and the
// HEAD-only reader (review of the last follow-ups, 2026-10-01). Moved as is.
import { computeReleaseManifestHash } from "./release-manifest-hash.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import { runReleaseFlowUpToG3 } from "./supervisor-release-flow.js";
import type { ResumeReleaseFlowInput } from "./supervisor-release-flow-resume-input.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";

/** Review → push → remote evidence → CI → G3 request (runReleaseFlowUpToG3). */
export async function resumeUpToG3(
  input: ResumeReleaseFlowInput,
  release: NonNullable<AutopilotConfig["release"]>,
  deployConfigured: boolean,
): Promise<ReleaseFlowResumeOutcome> {
  const reviewReport = await input.readWorkerReviewReport();
  if (!reviewReport) {
    return {
      kind: "waiting_review_report",
      reason:
        "Mission có config.release nhưng chưa có review report (chờ ticket review-local hoặc controller tự chạy secret-scan — chưa nối phiên này).",
    };
  }

  const { candidateSha, baseSha } = await input.readCandidateAndBaseSha();
  const flowResult = await runReleaseFlowUpToG3({
    store: input.store,
    controlRoot: input.controlRoot,
    missionId: input.missionId,
    repoDir: input.repoDir,
    candidateSha,
    baseSha,
    policyDigest: input.policyDigest,
    adapterDigest: input.adapterDigest,
    eventSeq: input.eventSeq,
    workerReviewReport: reviewReport,
    pushInput: input.pushInput,
    remoteEvidenceInput: input.remoteEvidenceInput,
    remoteEvidenceDeps: input.remoteEvidenceDeps,
    releaseManifestHash: computeReleaseManifestHash(candidateSha, release),
  });

  if (flowResult.status === "waiting_gate_g3") {
    const { g3RequestId, warning: reason } = flowResult;
    return { kind: "waiting_gate_g3", g3RequestId, deployConfigured, reason };
  }
  // CI gate (dogfood 2026-10-01): the pipeline is running/failed/not provable — G3 not asked.
  if (flowResult.status === "ci_not_passed") return flowResult.outcome;
  return { kind: "release_flow_blocked", reason: flowResult.reason };
}
